// src/components/TermsOfService.js
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';

const TermsOfService = ({ open, handleClose }) => {
  const today = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      BackdropProps={{
        sx: {
          backgroundColor: 'var(--modal-backdrop)',
          backdropFilter: 'blur(6px)'
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid var(--modal-border)',
          background: 'var(--modal-background)'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 3, md: 4 },
          py: 2.5,
          color: 'var(--modal-header-text)',
          background: 'var(--modal-header-gradient)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            backgroundColor: 'var(--modal-header-icon-bg)',
            display: 'grid',
            placeItems: 'center'
          }}
        >
          <GavelRoundedIcon />
        </Box>
        <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: 20, md: 24 }, flex: 1 }}>
          Terms of Service
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{ color: 'var(--modal-header-text)', '&:hover': { backgroundColor: 'var(--modal-header-hover)' } }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <DialogContent sx={{ px: { xs: 3, md: 4 }, py: { xs: 3, md: 4 } }}>
        <Typography variant="overline" sx={{ color: 'var(--text-muted)', letterSpacing: 1, mb: 1, display: 'block' }}>
          Amaze Puzzles™ — Last updated {today}
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          1) Acceptance of Terms
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          By accessing or using amazepuzzles.com, related subdomains, or any products and services we offer
          (collectively, the “Service”), you agree to be bound by these Terms of Service (“Terms”). If you do not agree,
          do not use the Service.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          2) Eligibility & Accounts
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          You must be at least 13 years old (or the age of digital consent in your jurisdiction) to use the Service.
          You are responsible for maintaining the confidentiality of your account credentials and for all activity under
          your account.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          3) Orders, Reservations & Payments
        </Typography>
        <Typography component="div" sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          By placing an order or reservation, you agree that:
          <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
            <li>All information provided is accurate and complete.</li>
            <li>Prices, availability, and promotions may change without notice.</li>
            <li>
              Reservation deposits (e.g., nominal fees to reserve upcoming puzzles) hold a priority purchase spot and
              are not a guarantee of final purchase until the full order is completed.
            </li>
            <li>We may cancel or limit quantities at our discretion, including suspected fraud or misuse.</li>
          </Box>
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          4) Shipping, Returns & Refunds
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          Shipping timelines are estimates and can vary. If your item arrives damaged or you encounter an issue, contact
          us promptly so we can help. Certain items may be final‑sale (e.g., digital downloads, limited releases, or
          opened educational materials); see product pages for specifics.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          5) Acceptable Use
        </Typography>
        <Typography component="div" sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          You agree not to misuse the Service. Prohibited activities include (without limitation):
          <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
            <li>Violating any applicable law or third‑party rights;</li>
            <li>Reverse‑engineering, scraping, or excessive automated access;</li>
            <li>Uploading malicious code or interfering with normal operation;</li>
            <li>Impersonation or misrepresenting your affiliation;</li>
            <li>Any activity that could harm the accessibility or integrity of the Service.</li>
          </Box>
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          6) Intellectual Property
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          All content on the Service—including text, graphics, logos, product designs, and software—is owned by or
          licensed to Amaze Puzzles and is protected by intellectual property laws. You may not reproduce, distribute,
          or create derivative works without our prior written consent.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          7) User Content & Feedback
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          If you submit content or feedback, you grant Amaze Puzzles a non‑exclusive, worldwide, royalty‑free license
          to use, reproduce, and display such content for operating and improving the Service. You represent you have
          the rights to grant this license and that your content does not infringe any third‑party rights.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          8) Third‑Party Links & Services
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          The Service may contain links to third‑party sites or services (e.g., social platforms, payment processors).
          We are not responsible for their content or practices. Use third‑party sites at your own risk and review their
          terms and policies.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          9) Disclaimers
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          The Service is provided “as is” and “as available” without warranties of any kind, express or implied.
          Amaze Puzzles disclaims all warranties of merchantability, fitness for a particular purpose, and
          non‑infringement to the fullest extent permitted by law.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          10) Limitation of Liability
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          To the maximum extent permitted by law, Amaze Puzzles shall not be liable for any indirect, incidental,
          special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or
          indirectly, arising from your use of the Service.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          11) Indemnification
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          You agree to indemnify and hold harmless Amaze Puzzles and its affiliates, officers, employees, and agents
          from any claims, liabilities, damages, losses, and expenses (including reasonable attorneys’ fees) arising out
          of or in any way connected with your use of the Service or violation of these Terms.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          12) Changes to these Terms
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          We may update these Terms from time to time. Changes are effective when posted on the Service. Continued use
          after changes constitutes acceptance of the updated Terms.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--text-primary-dark)', mb: 1 }}>
          13) Governing Law
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 3 }}>
          These Terms are governed by the laws of the State of Washington, USA, without regard to conflict‑of‑law
          principles, unless a different jurisdiction is required by applicable law.
        </Typography>

        <Divider sx={{ mt: 3, mb: 0 }} />
        <Typography variant="body2" sx={{ display: 'block', color: 'var(--text-muted)', mt: 1.5 }}>
          This document is provided for general information and is not legal advice.
        </Typography>
      </DialogContent>

      {/* Footer actions */}
      <DialogActions sx={{ px: { xs: 3, md: 4 }, pb: 3, pt: 0 }}>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            fontWeight: 800,
            borderRadius: 2,
            textTransform: 'none',
            background: 'var(--modal-header-gradient)',
            '&:hover': { opacity: 0.95 }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsOfService;
